import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import {
    Trash2, Eye, X, Search, Plus, PhoneCall, Upload, FileText,
    Loader2, ArrowLeft, Pencil, Check, ChevronRight, MapPin, Wrench,
    ClipboardCheck, Link2, ExternalLink, FileCheck, CheckCircle2,
    Fingerprint, Hammer, Camera, Trophy, Calendar, Image as ImageIcon,
} from "lucide-react";
import { resizeImageForEmail } from "@/lib/imageResize";
import LeadInternalDocs from "@/components/admin/LeadInternalDocs";
import { NewLeadPhotoPicker, LeadPhotosSection } from "@/components/admin/LeadPhotos";
import { LeadCoverPhoto } from "@/components/admin/LeadCoverPhoto";
import { LeadConversations } from "@/components/admin/LeadConversations";

const STATUSES = ["Novo", "Contactado", "Visita agendada", "Orçamento em preparação", "Orçamento", "Proposta disponível", "Adjudicado", "Execução", "Não adjudicado", "Concluído"];
const WORK_TYPES = ["Remodelação completa","Remodelação parcial","Cozinha","Casa de banho","Sala","Quarto","Pinturas","Pavimentos","Canalização","Eletricidade","Carpintaria","Outros"];
const PROPERTY_TYPES = ["Apartamento", "Moradia", "Comercial", "Outro"];

const STATUS_COLORS = {
    "Novo": "bg-blue-100 text-blue-800",
    "Contactado": "bg-indigo-100 text-indigo-800",
    "Visita agendada": "bg-purple-100 text-purple-800",
    "Orçamento em preparação": "bg-orange-100 text-orange-800",
    "Orçamento": "bg-amber-100 text-amber-800",
    "Proposta disponível": "bg-teal-100 text-teal-800",
    "Adjudicado": "bg-emerald-100 text-emerald-800",
    "Execução": "bg-amber-100 text-amber-800",
    "Não adjudicado": "bg-stone-200 text-stone-700",
    "Concluído": "bg-green-100 text-green-800",
};

/* ================================================================
   Lista de Pedidos / Contactos
   Desktop: tabela (mantida).
   Mobile:  cards verticais, sem scroll horizontal.
   ================================================================ */
export default function AdminLeads({ scope = "inbox" }) {
    const isClientsView = scope === "clients";
    const [searchParams, setSearchParams] = useSearchParams();
    const [leads, setLeads] = useState([]);
    const [selected, setSelected] = useState(null);
    const [filter, setFilter] = useState("todos");
    // Dashboard → "Mensagens por responder" abre Clientes com ?filtro=por-responder.
    const [awaitingOnly, setAwaitingOnly] = useState(searchParams.get("filtro") === "por-responder");
    const [q, setQ] = useState("");
    const [creating, setCreating] = useState(null);
    const [newPhotosBusy, setNewPhotosBusy] = useState(false);
    const [uploadingQuote, setUploadingQuote] = useState(null);

    const load = useCallback(async () => {
        const params = {};
        if (filter !== "todos") params.status = filter;
        if (q.trim()) params.q = q.trim();
        const { data } = await api.get("/admin/leads", { params });
        // Scope filter — single source of truth (same `leads` collection,
        // same API, same records). "inbox" shows only `status === "Novo"`
        // (or leads without a status, treated as new). "clients" shows
        // everything else, including "Concluído" and "Não adjudicado",
        // so the operator can keep accompanying each record end-to-end.
        const scoped = isClientsView
            ? data.filter((l) => l.status && l.status !== "Novo" && (!awaitingOnly || l.awaiting_reply > 0))
            : data.filter((l) => !l.status || l.status === "Novo");
        setLeads(scoped);
    }, [filter, q, isClientsView, awaitingOnly]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (searchParams.get("filtro")) setSearchParams({}, { replace: true });
    }, [searchParams, setSearchParams]);
    const pickFilter = (s) => { setFilter(s); setAwaitingOnly(false); };
    const emptyText = !isClientsView ? "Sem pedidos novos." : awaitingOnly ? "Nenhum cliente à espera de resposta." : "Sem clientes a acompanhar.";

    // Deep-link: ?lead=<id> abre a ficha directamente (Obras em curso → Abrir, email da questão).
    const openLeadId = searchParams.get("lead");
    useEffect(() => {
        if (!openLeadId) return;
        api.get("/admin/leads").then(({ data }) => {
            const hit = data.find((l) => l.id === openLeadId);
            if (hit) setSelected(hit);
        }).catch(() => {});
        setSearchParams({}, { replace: true });
    }, [openLeadId, setSearchParams]);

    const update = async (id, patch) => {
        await api.patch(`/admin/leads/${id}`, patch);
        load();
        if (selected?.id === id) setSelected({ ...selected, ...patch });
    };

    const del = async (id) => {
        if (!window.confirm("Eliminar pedido?")) return;
        await api.delete(`/admin/leads/${id}`);
        toast.success("Eliminado");
        setSelected(null);
        load();
    };

    const attachQuote = async (id, file) => {
        if (!file) return;
        if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
            return toast.error("Apenas ficheiros PDF");
        }
        setUploadingQuote(id);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await api.patch(`/admin/leads/${id}`, { quote_file_id: data.id, quote_filename: file.name });
            toast.success("Orçamento anexado");
            load();
            if (selected?.id === id) setSelected({ ...selected, quote_file_id: data.id, quote_filename: file.name });
        } catch { toast.error("Erro ao anexar"); }
        finally { setUploadingQuote(null); }
    };

    const detachQuote = async (id) => {
        if (!window.confirm("Remover orçamento anexado?")) return;
        await api.patch(`/admin/leads/${id}`, { quote_file_id: null, quote_filename: null });
        toast.success("Orçamento removido");
        load();
        if (selected?.id === id) setSelected({ ...selected, quote_file_id: null, quote_filename: null });
    };

    const submitNew = async (e) => {
        e.preventDefault();
        if (!creating.name || !creating.phone) return toast.error("Nome e telefone são obrigatórios");
        if (newPhotosBusy) return toast.error("Aguarde o fim do carregamento das fotografias");
        try {
            await api.post("/admin/leads", creating);
            toast.success("Pedido registado");
            setCreating(null);
            load();
        } catch { toast.error("Erro ao registar"); }
    };

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <h1 className="font-display text-3xl font-bold text-stone-900" data-testid="leads-page-title">
                    {isClientsView ? "Clientes" : "Pedidos / Contactos"}
                </h1>
                {!isClientsView && (
                    <button
                        onClick={() => setCreating({ name:"", phone:"", email:"", location:"", property_type:"", work_types:[], timing:"", budget:"", description:"", file_ids:[] })}
                        className="btn-copper !py-2 !px-4 text-sm" data-testid="lead-new-btn">
                        <Plus className="w-4 h-4" /> Registar contacto manual
                    </button>
                )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
                <div className="relative flex-1">
                    <Search className="w-4 h-4 absolute left-3 top-3 text-stone-400" />
                    <input value={q} onChange={(e) => setQ(e.target.value)}
                        placeholder="Pesquisar por nome, email, localização, descrição…"
                        className="w-full pl-10 pr-4 py-2.5 border border-stone-300 rounded-full text-sm"
                        data-testid="leads-search" />
                </div>
            </div>

            {/* Filtros — scroll horizontal em mobile só nesta barra (não na lista). */}
            {/* Inbox: só existe "Novo", filtros ocultos. Clientes: todos os estados excepto "Novo". */}
            {isClientsView && (
                <div className="flex flex-nowrap md:flex-wrap gap-2 mb-6 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 pb-1">
                    <button onClick={() => pickFilter("todos")} data-testid="filter-todos" className={pill(filter === "todos" && !awaitingOnly)}>Todos</button>
                    <button onClick={() => { setFilter("todos"); setAwaitingOnly(true); }} data-testid="filter-por-responder" className={pill(awaitingOnly)}>Por responder</button>
                    {STATUSES.filter((s) => s !== "Novo").map((s) => (
                        <button key={s} onClick={() => pickFilter(s)} data-testid={`filter-${s}`} className={pill(filter === s && !awaitingOnly)}>{s}</button>
                    ))}
                </div>
            )}

            {/* Mobile: cards verticais (sem scroll horizontal) */}
            <div className="md:hidden space-y-3" data-testid="leads-mobile-list">
                {leads.map((l) => <LeadCard key={l.id} lead={l} onOpen={setSelected} />)}
                {leads.length === 0 && (
                    <div className="bg-white rounded-xl border border-stone-200 p-10 text-center text-stone-400" data-testid="leads-empty-mobile">
                        {emptyText}
                    </div>
                )}
            </div>

            {/* Desktop: tabela (preservada tal como estava) */}
            <div className="hidden md:block bg-white rounded-xl border border-stone-200 overflow-x-auto">
                <table className="w-full text-sm min-w-[720px]">
                    <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
                        <tr>
                            <th className="text-left px-4 py-3">Data</th>
                            <th className="text-left px-4 py-3">Nome</th>
                            <th className="text-left px-4 py-3">Localização</th>
                            <th className="text-left px-4 py-3">Trabalhos</th>
                            <th className="text-left px-4 py-3">Estado</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                        {leads.map(l => (
                            <tr key={l.id} className="hover:bg-stone-50">
                                <td className="px-4 py-3 text-stone-500 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString("pt-PT")}</td>
                                <td className="px-4 py-3 font-medium">
                                    {l.name}
                                    {l.unread_conversations > 0 && <UnreadBadge id={l.id} />}
                                    <div className="text-xs text-stone-500">{l.phone}</div>
                                </td>
                                <td className="px-4 py-3">{l.location}</td>
                                <td className="px-4 py-3 text-xs text-stone-600">
                                    {(l.work_types || []).slice(0,2).join(", ")}
                                    {(l.work_types || []).length > 2 ? "…" : ""}
                                </td>
                                <td className="px-4 py-3">
                                    <select value={l.status} onChange={(e) => update(l.id, { status: e.target.value })}
                                        className="text-xs border border-stone-200 rounded px-2 py-1 bg-white">
                                        {STATUSES.map(s => <option key={s}>{s}</option>)}
                                    </select>
                                </td>
                                <td className="px-4 py-3 text-right whitespace-nowrap">
                                    <button onClick={() => setSelected(l)} data-testid={`view-${l.id}`} className="p-1 hover:text-[#B56D3B]"><Eye className="w-4 h-4" /></button>
                                    <button onClick={() => del(l.id)} className="p-1 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                                </td>
                            </tr>
                        ))}
                        {leads.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-stone-400" data-testid="leads-empty">{emptyText}</td></tr>}
                    </tbody>
                </table>
            </div>

            {selected && (
                <LeadDetail
                    key={selected.id}
                    selected={selected}
                    setSelected={setSelected}
                    reload={load}
                    uploadingQuote={uploadingQuote}
                    attachQuote={attachQuote}
                    detachQuote={detachQuote}
                    onDelete={del}
                />
            )}

            {/* Criação manual — não abrangida pelo redesign mobile (fora de scope). */}
            {creating && (
                <div className="fixed inset-0 bg-stone-950/60 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setCreating(null)}>
                    <form onSubmit={submitNew} className="bg-white rounded-xl max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="lead-new-modal">
                        <div className="p-6 border-b border-stone-100 flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <PhoneCall className="w-5 h-5 text-[hsl(var(--copper))]" />
                                <h2 className="font-display font-bold text-xl">Novo contacto (entrada manual)</h2>
                            </div>
                            <button type="button" onClick={() => setCreating(null)}><X className="w-5 h-5" /></button>
                        </div>
                        <div className="p-6 space-y-3">
                            <p className="text-xs text-stone-500">Use este formulário para registar clientes que contactam por telefone, presencialmente ou WhatsApp. Não são enviados emails.</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <F label="Nome *" v={creating.name} on={(v) => setCreating({...creating, name: v})} testid="ln-name" />
                                <F label="Telefone *" v={creating.phone} on={(v) => setCreating({...creating, phone: v})} type="tel" testid="ln-phone" />
                                <F label="Email" v={creating.email} on={(v) => setCreating({...creating, email: v})} type="email" />
                                <F label="Localização" v={creating.location} on={(v) => setCreating({...creating, location: v})} />
                                <label className="block text-sm">
                                    <span className="text-xs uppercase tracking-wider text-stone-500">Tipo de imóvel</span>
                                    <select value={creating.property_type} onChange={(e) => setCreating({...creating, property_type: e.target.value})} className="w-full mt-1 px-3 py-2 border border-stone-300 rounded text-sm bg-white">
                                        <option value="">—</option>
                                        {PROPERTY_TYPES.map(p => <option key={p}>{p}</option>)}
                                    </select>
                                </label>
                                <F label="Timing" v={creating.timing} on={(v) => setCreating({...creating, timing: v})} placeholder="ex. próximas 4 semanas" />
                                <F label="Orçamento estimado" v={creating.budget} on={(v) => setCreating({...creating, budget: v})} placeholder="opcional" />
                            </div>
                            <div>
                                <span className="text-xs uppercase tracking-wider text-stone-500 block mb-2">Trabalhos</span>
                                <div className="flex flex-wrap gap-1.5">
                                    {WORK_TYPES.map(w => {
                                        const on = creating.work_types.includes(w);
                                        return (
                                            <button type="button" key={w}
                                                onClick={() => setCreating({...creating, work_types: on ? creating.work_types.filter(x => x !== w) : [...creating.work_types, w]})}
                                                className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-[hsl(var(--forest))] text-white border-[hsl(var(--forest))]" : "bg-white text-stone-700 border-stone-300"}`}
                                            >{w}</button>
                                        );
                                    })}
                                </div>
                            </div>
                            <label className="block text-sm">
                                <span className="text-xs uppercase tracking-wider text-stone-500">Descrição</span>
                                <textarea rows={3} value={creating.description} onChange={(e) => setCreating({...creating, description: e.target.value})} className="w-full mt-1 px-3 py-2 border border-stone-300 rounded text-sm" />
                            </label>
                            <NewLeadPhotoPicker
                                ids={creating.file_ids || []}
                                onChange={(fn) => setCreating((c) => ({ ...c, file_ids: fn(c.file_ids || []) }))}
                                onBusy={setNewPhotosBusy}
                            />
                            <div className="flex gap-2 pt-2">
                                <button type="submit" disabled={newPhotosBusy} className="btn-copper flex-1 justify-center disabled:opacity-50" data-testid="ln-save">Registar</button>
                                <button type="button" onClick={() => setCreating(null)} className="px-6 py-2 border border-stone-300 rounded-full">Cancelar</button>
                            </div>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

/* ---------------- Mobile card ---------------- */
function LeadCard({ lead, onOpen }) {
    return (
        <button
            type="button"
            onClick={() => onOpen(lead)}
            className="w-full text-left bg-white rounded-xl border border-stone-200 p-4 hover:border-stone-300 active:bg-stone-50 transition"
            data-testid={`lead-card-${lead.id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                    <div className="font-medium text-stone-900 truncate">{lead.name}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{lead.phone}</div>
                    {lead.unread_conversations > 0 && <UnreadBadge id={lead.id} mobile />}
                </div>
                <StatusChip status={lead.status} />
            </div>
            <div className="mt-2 space-y-1 text-xs text-stone-600">
                {lead.location && (
                    <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-stone-400 flex-shrink-0" /><span className="truncate">{lead.location}</span></div>
                )}
                {(lead.work_types || []).length > 0 && (
                    <div className="flex items-start gap-1.5">
                        <Wrench className="w-3 h-3 text-stone-400 flex-shrink-0 mt-[3px]" />
                        <span className="truncate">
                            {lead.work_types.slice(0,3).join(", ")}
                            {lead.work_types.length > 3 ? " …" : ""}
                        </span>
                    </div>
                )}
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-stone-500">
                <span>{new Date(lead.created_at).toLocaleDateString("pt-PT")}</span>
                <ChevronRight className="w-4 h-4 text-stone-400" />
            </div>
        </button>
    );
}

function UnreadBadge({ id, mobile }) {
    return (
        <span className="ml-2 inline-flex items-center gap-1 align-middle text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[hsl(var(--copper))] text-white whitespace-nowrap" data-testid={`lead-unread-${mobile ? "m-" : ""}${id}`}>
            Nova mensagem
        </span>
    );
}

function StatusChip({ status }) {
    return (
        <span className={`text-[10px] uppercase tracking-wider font-medium px-2 py-1 rounded-full whitespace-nowrap ${STATUS_COLORS[status] || "bg-stone-100 text-stone-700"}`}>
            {status || "—"}
        </span>
    );
}

/* ---------------- Bloco 2: File slot inside Proposta card ---------------- */
function FileSlot({ label, fileId, filename, slot, patchField, required }) {
    const [uploading, setUploading] = useState(false);
    const attach = async (file) => {
        if (!file) return;
        if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
            return toast.error("Apenas ficheiros PDF");
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            await patchField({ [`${slot}_file_id`]: data.id, [`${slot}_filename`]: file.name });
            toast.success("Documento anexado");
        } catch { toast.error("Erro ao anexar"); }
        finally { setUploading(false); }
    };
    const detach = async () => {
        if (!window.confirm(`Remover ${label}?`)) return;
        await patchField({ [`${slot}_file_id`]: null, [`${slot}_filename`]: null });
    };
    return (
        <div className="flex items-center gap-3 border border-stone-200 rounded-lg p-3" data-testid={`lead-proposal-slot-${slot}`}>
            <div className={`flex items-center justify-center w-9 h-9 rounded-full flex-shrink-0 ${fileId ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-400"}`}>
                <FileText className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{label}</span>
                    <span
                        className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${required ? "bg-[hsl(var(--copper))]/10 text-[hsl(var(--copper))]" : "bg-stone-100 text-stone-500"}`}
                        data-testid={`lead-proposal-badge-${slot}`}
                    >
                        {required ? "Obrigatório" : "Opcional"}
                    </span>
                </div>
                {fileId ? (
                    <a href={fileUrl(fileId)} target="_blank" rel="noreferrer" className="text-sm text-stone-900 hover:underline truncate block" data-testid={`lead-proposal-view-${slot}`}>
                        {filename || `${label}.pdf`}
                    </a>
                ) : (
                    <div className="text-xs text-stone-400 italic">Nenhum ficheiro carregado.</div>
                )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
                {fileId ? (
                    <>
                        <label className="text-xs text-stone-700 hover:underline cursor-pointer py-1" data-testid={`lead-proposal-replace-${slot}`}>
                            {uploading ? "..." : "Substituir"}
                            <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => e.target.files[0] && attach(e.target.files[0])} />
                        </label>
                        <button type="button" onClick={detach} className="text-xs text-red-600 hover:underline" data-testid={`lead-proposal-remove-${slot}`}>Remover</button>
                    </>
                ) : (
                    <label className="text-xs bg-stone-100 hover:bg-stone-200 px-3 py-1.5 rounded-full cursor-pointer" data-testid={`lead-proposal-upload-${slot}`}>
                        {uploading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : "Carregar"}
                        <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => e.target.files[0] && attach(e.target.files[0])} />
                    </label>
                )}
            </div>
        </div>
    );
}

/* ---------------- Helpers ---------------- */
const F = ({ label, v, on, onBlur, type = "text", placeholder, testid }) => (
    <label className="block text-sm">
        <span className="text-xs uppercase tracking-wider text-stone-500">{label}</span>
        <input
            type={type}
            value={v}
            onChange={(e) => on(e.target.value)}
            onBlur={onBlur}
            placeholder={placeholder}
            data-testid={testid}
            inputMode={type === "tel" ? "tel" : (type === "email" ? "email" : "text")}
            className="w-full mt-1 px-3 py-3 md:py-2 border border-stone-300 rounded text-sm"
        />
    </label>
);
const pill = (a) => `px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 ${a ? "bg-[#18352F] text-white" : "bg-white border border-stone-200 text-stone-700 hover:border-stone-400"}`;

function ViewRow({ k, v, multiline }) {
    return (
        <div className="py-2.5 border-b border-stone-100 last:border-0">
            <div className="text-[11px] uppercase tracking-wider text-stone-500">{k}</div>
            <div className={`text-sm text-stone-900 mt-0.5 ${multiline ? "whitespace-pre-wrap break-words" : "break-words"}`}>
                {v ? v : <span className="text-stone-400">—</span>}
            </div>
        </div>
    );
}
function ViewSection({ title, children }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-2 font-medium">{title}</div>
            <div className="bg-white rounded-lg border border-stone-200 px-3">
                {children}
            </div>
        </div>
    );
}
function EditSection({ title, children }) {
    return (
        <div>
            <div className="text-xs uppercase tracking-wider text-stone-500 mb-2 font-medium">{title}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {children}
            </div>
        </div>
    );
}

/* ================================================================
   Ficha do Pedido / Contacto
   Mobile: página inteira, sem backdrop clicável (backup contra
   fechos acidentais). Autosave onBlur + toast erro. Consulta/edit
   como modos separados.
   Desktop: modal centrado (comportamento original mantido).
   ================================================================ */
function LeadDetail({ selected, setSelected, reload, uploadingQuote, attachQuote, detachQuote, onDelete }) {
    const [draft, setDraft] = useState(selected);
    const [mode, setMode] = useState("view"); // "view" | "edit"
    const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved
    const [copyingLink, setCopyingLink] = useState(false);
    const [linkPreview, setLinkPreview] = useState(null);
    const savedRef = useRef(selected);
    const hideTimer = useRef(null);

    // Reset when a different lead is opened
    useEffect(() => {
        setDraft(selected);
        savedRef.current = selected;
        setMode("view");
    }, [selected?.id]);

    // Auto-hide "Guardado ✓" after ~1.8s
    useEffect(() => {
        if (saveStatus !== "saved") return;
        clearTimeout(hideTimer.current);
        hideTimer.current = setTimeout(() => setSaveStatus("idle"), 1800);
        return () => clearTimeout(hideTimer.current);
    }, [saveStatus]);

    const patchField = async (patch) => {
        setSaveStatus("saving");
        try {
            const { data } = await api.patch(`/admin/leads/${selected.id}`, patch);
            savedRef.current = { ...savedRef.current, ...patch, ...(data?.applied || {}) };
            setSelected({ ...savedRef.current });
            setSaveStatus("saved");
            if (data?.applied?.status) reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao guardar");
            setSaveStatus("idle");
        }
    };

    // Save on blur: only PATCH if actually different from last-saved value
    const saveField = (key) => {
        const cur = draft?.[key];
        const saved = savedRef.current?.[key];
        if (JSON.stringify(cur ?? "") === JSON.stringify(saved ?? "")) return;
        patchField({ [key]: cur ?? "" });
    };

    // For selects / chip toggles: update draft AND fire save immediately
    const setFieldAndSave = (key, val) => {
        setDraft((d) => ({ ...d, [key]: val }));
        patchField({ [key]: val });
    };

    const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

    const toggleWork = (w) => {
        const cur = draft.work_types || [];
        const next = cur.includes(w) ? cur.filter((x) => x !== w) : [...cur, w];
        setFieldAndSave("work_types", next);
    };

    // ---- Bloco 1: Acompanhamento do cliente ----
    const setProposalDate = (val) => setFieldAndSave("proposal_delivery_date", val || null);
    const bumpProposalDate = (days) => {
        const d = new Date();
        d.setDate(d.getDate() + days);
        setProposalDate(d.toISOString().slice(0, 10));
    };
    const copyClientLink = async () => {
        setCopyingLink(true);
        try {
            const { data } = await api.get(`/admin/leads/${selected.id}/client-link`);
            setLinkPreview(data.url);
            try {
                await navigator.clipboard.writeText(data.url);
                toast.success("Link privado copiado");
            } catch {
                // Fallback for older mobile Safari
                const ta = document.createElement("textarea");
                ta.value = data.url;
                ta.style.position = "fixed";
                ta.style.left = "-9999px";
                document.body.appendChild(ta);
                ta.select();
                let ok = false;
                try { ok = document.execCommand("copy"); } catch { ok = false; }
                document.body.removeChild(ta);
                if (ok) toast.success("Link privado copiado");
                else toast.message("Copie manualmente a partir da caixa abaixo");
            }
        } catch {
            toast.error("Erro ao obter link");
        } finally {
            setCopyingLink(false);
        }
    };
    const openClientArea = async () => {
        try {
            const { data } = await api.get(`/admin/leads/${selected.id}/client-link`);
            window.open(data.url, "_blank", "noopener,noreferrer");
        } catch {
            toast.error("Erro ao abrir Área do Cliente");
        }
    };

    // ---- Bloco 2: Proposta e Adjudicação ----
    const [publishingProposal, setPublishingProposal] = useState(false);
    // Immutable adjudication snapshot (sha256 audit trail). Loaded lazily
    // only after a lead is in "Adjudicado" state. 404 is treated as "no
    // snapshot available" and the panel just stays hidden.
    const [snapshot, setSnapshot] = useState(null);
    useEffect(() => {
        if (!selected?.id || selected?.status !== "Adjudicado") {
            setSnapshot(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/admin/leads/${selected.id}/adjudication`);
                if (!cancelled) setSnapshot(data);
            } catch {
                if (!cancelled) setSnapshot(null);
            }
        })();
        return () => { cancelled = true; };
    }, [selected?.id, selected?.status]);

    const publishProposal = async () => {
        if (!selected.proposal_file_id) {
            return toast.error("Carregue a Proposta / Orçamento antes de publicar.");
        }
        if (!window.confirm("Publicar a proposta? O cliente vai receber email e ver os documentos na sua Área.")) return;
        setPublishingProposal(true);
        try {
            const { data } = await api.post(`/admin/leads/${selected.id}/publish-proposal`);
            savedRef.current = { ...savedRef.current, status: "Proposta disponível", published_at: data.published_at, has_doubt: false };
            setSelected({ ...savedRef.current });
            toast.success("Proposta publicada. Cliente notificado.");
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao publicar");
        } finally { setPublishingProposal(false); }
    };

    // ---- Bloco 3A: Execução, atualizações e conclusão ----
    const [startingExecution, setStartingExecution] = useState(false);
    const [completingExecution, setCompletingExecution] = useState(false);
    const [updates, setUpdates] = useState([]);
    const [newUpdateOpen, setNewUpdateOpen] = useState(false);
    const [editingUpdate, setEditingUpdate] = useState(null);
    const [showAllUpdates, setShowAllUpdates] = useState(false);

    useEffect(() => {
        if (!selected?.id) { setUpdates([]); return; }
        const s = selected?.status;
        if (s !== "Execução" && s !== "Concluído") { setUpdates([]); return; }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/admin/leads/${selected.id}/updates`);
                if (!cancelled) setUpdates(data || []);
            } catch {
                if (!cancelled) setUpdates([]);
            }
        })();
        return () => { cancelled = true; };
    }, [selected?.id, selected?.status]);

    const reloadUpdates = async () => {
        try {
            const { data } = await api.get(`/admin/leads/${selected.id}/updates`);
            setUpdates(data || []);
        } catch { /* keep previous */ }
    };

    const [deletingUpdateId, setDeletingUpdateId] = useState(null);
    const deleteUpdate = async (u) => {
        if (!window.confirm("Apagar esta atualização?\n\nDeixa de aparecer no Backoffice e na Área do Cliente. Fica guardada internamente, com as fotografias, e pode ser recuperada se for um engano.")) return;
        setDeletingUpdateId(u.id);
        try {
            await api.delete(`/admin/leads/${selected.id}/updates/${u.id}`);
            setUpdates((cur) => cur.filter((x) => x.id !== u.id));
            toast.success("Atualização apagada.");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao apagar a atualização");
        } finally {
            setDeletingUpdateId(null);
        }
    };

    const onConversationResolved = (hasDoubt) => {
        savedRef.current = { ...savedRef.current, has_doubt: hasDoubt };
        setSelected({ ...savedRef.current });
        reload();
    };

    const startExecution = async () => {
        if (!window.confirm("Iniciar execução desta obra? O cliente passa a ver 'Execução em curso' na sua Área.")) return;
        setStartingExecution(true);
        try {
            const { data } = await api.post(`/admin/leads/${selected.id}/start-execution`);
            savedRef.current = { ...savedRef.current, status: "Execução", execution_started_at: data.execution_started_at };
            setSelected({ ...savedRef.current });
            toast.success("Execução iniciada.");
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao iniciar execução");
        } finally { setStartingExecution(false); }
    };

    const completeExecution = async () => {
        if (!window.confirm("Marcar a obra como concluída? O cliente recebe um email de conclusão e o estado fica Concluído.")) return;
        setCompletingExecution(true);
        try {
            const { data } = await api.post(`/admin/leads/${selected.id}/complete`);
            savedRef.current = { ...savedRef.current, status: "Concluído", completed_at: data.completed_at };
            setSelected({ ...savedRef.current });
            toast.success("Obra concluída. Email enviado ao cliente.");
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erro ao concluir");
        } finally { setCompletingExecution(false); }
    };

    const close = () => {
        // Force blur so any pending onBlur (last text field the user was
        // typing in) fires BEFORE we unmount. Without this, iOS Safari
        // sometimes swallows the last-field edit on modal close.
        if (typeof document !== "undefined" && document.activeElement && typeof document.activeElement.blur === "function") {
            document.activeElement.blur();
        }
        setTimeout(() => {
            reload();
            setSelected(null);
        }, 120);
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-white md:bg-stone-950/60 md:flex md:items-center md:justify-center md:p-4"
            onClick={(e) => {
                // Backdrop tap closes ONLY on desktop, and ONLY when the tap
                // hits the overlay itself (not any inner content). This
                // eliminates the mobile bug where dismissing the keyboard
                // accidentally closed the modal.
                const isDesktop = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(min-width: 768px)").matches;
                if (isDesktop && e.target === e.currentTarget) close();
            }}
        >
            <div
                className="bg-white w-full h-full md:h-auto md:max-w-2xl md:max-h-[90vh] md:rounded-xl md:shadow-2xl flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                data-testid="lead-detail-modal"
            >
                {/* Sticky header — sempre visível, contém back + nome + estado + indicador de save. */}
                <div className="sticky top-0 z-10 bg-white border-b border-stone-100 px-3 py-2.5 md:px-6 md:py-4 flex items-center gap-3">
                    <button
                        type="button"
                        onClick={close}
                        aria-label="Voltar"
                        className="p-2 -ml-1 rounded-full hover:bg-stone-100 min-w-[44px] min-h-[44px] flex items-center justify-center"
                        data-testid="lead-back"
                    >
                        <ArrowLeft className="w-5 h-5 md:hidden" />
                        <X className="w-5 h-5 hidden md:block" />
                    </button>
                    <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-lg md:text-2xl truncate leading-tight">{selected.name}</div>
                        <div className="text-xs text-stone-500 flex items-center gap-2 flex-wrap mt-0.5">
                            <StatusChip status={selected.status} />
                            {saveStatus === "saving" && (
                                <span className="text-stone-500 flex items-center gap-1" data-testid="save-indicator-saving">
                                    <Loader2 className="w-3 h-3 animate-spin" /> A guardar…
                                </span>
                            )}
                            {saveStatus === "saved" && (
                                <span className="text-emerald-600 flex items-center gap-1" data-testid="save-indicator-saved">
                                    <Check className="w-3 h-3" /> Guardado
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Body scrollável */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-5 pb-24">
                    {/* Estado + data de criação */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        <label className="block text-sm flex-1">
                            <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Estado</span>
                            <select
                                value={draft.status || "Novo"}
                                onChange={(e) => setFieldAndSave("status", e.target.value)}
                                className="w-full px-3 py-3 md:py-2 border border-stone-300 rounded text-sm bg-white"
                                data-testid="lead-detail-status"
                            >
                                {STATUSES.map((s) => <option key={s}>{s}</option>)}
                            </select>
                        </label>
                        <div className="text-xs text-stone-500 sm:self-end sm:pb-3">
                            Criado em {new Date(selected.created_at).toLocaleString("pt-PT")}
                        </div>
                    </div>

                    {/* ============ ACOMPANHAMENTO DO CLIENTE (Bloco 1) ============ */}
                    <div className="bg-white rounded-lg border border-stone-200 p-4 md:p-5" data-testid="lead-tracking-section">
                        <div className="flex items-center gap-2 mb-2">
                            <ClipboardCheck className="w-4 h-4 text-[hsl(var(--copper))]" />
                            <span className="text-xs uppercase tracking-wider text-stone-700 font-semibold">
                                Acompanhamento do cliente
                            </span>
                        </div>
                        <p className="text-xs text-stone-500 mb-4">
                            O cliente é notificado por email a cada alteração relevante e pode consultar tudo através do seu link privado.
                        </p>

                        <div className="mb-4">
                            <label className="block text-sm">
                                <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">
                                    Entrega prevista da proposta
                                </span>
                                <div className="flex flex-wrap gap-2">
                                    <input
                                        type="date"
                                        value={draft.proposal_delivery_date || ""}
                                        onChange={(e) => setProposalDate(e.target.value)}
                                        className="flex-1 min-w-[160px] px-3 py-3 md:py-2 border border-stone-300 rounded text-sm bg-white"
                                        data-testid="lead-proposal-date"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => bumpProposalDate(3)}
                                        className="px-3 py-2 rounded-lg border border-stone-300 text-xs text-stone-700 hover:border-[hsl(var(--copper))] hover:text-[hsl(var(--copper))] transition"
                                        data-testid="lead-proposal-date-3"
                                    >+3d</button>
                                    <button
                                        type="button"
                                        onClick={() => bumpProposalDate(5)}
                                        className="px-3 py-2 rounded-lg border border-stone-300 text-xs text-stone-700 hover:border-[hsl(var(--copper))] hover:text-[hsl(var(--copper))] transition"
                                        data-testid="lead-proposal-date-5"
                                    >+5d</button>
                                    <button
                                        type="button"
                                        onClick={() => bumpProposalDate(7)}
                                        className="px-3 py-2 rounded-lg border border-stone-300 text-xs text-stone-700 hover:border-[hsl(var(--copper))] hover:text-[hsl(var(--copper))] transition"
                                        data-testid="lead-proposal-date-7"
                                    >+7d</button>
                                </div>
                            </label>
                            {draft.status === "Orçamento em preparação" && !draft.proposal_delivery_date && (
                                <p className="text-xs text-amber-700 mt-2">
                                    Defina uma data para que o cliente saiba quando esperar a proposta.
                                </p>
                            )}
                        </div>

                        <label className="block text-sm mb-4">
                            <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">
                                Nota para o cliente
                                <span className="text-stone-400 normal-case tracking-normal ml-1 text-[10px]">(visível na Área do Cliente)</span>
                            </span>
                            <textarea
                                rows={3}
                                value={draft.client_note || ""}
                                onChange={(e) => setField("client_note", e.target.value)}
                                onBlur={() => saveField("client_note")}
                                placeholder="Ex.: A entrega ficou adiada 2 dias porque estamos a fazer uma consulta técnica adicional aos materiais."
                                className="w-full px-3 py-2 border border-stone-300 rounded text-sm resize-none"
                                data-testid="lead-client-note"
                            />
                            <p className="text-xs text-stone-500 mt-1">
                                Deixe em branco para não mostrar nenhuma nota. Guarda ao sair do campo.
                            </p>
                        </label>

                        <div className="flex flex-col sm:flex-row gap-2">
                            <button
                                type="button"
                                onClick={copyClientLink}
                                disabled={copyingLink}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-stone-300 text-sm font-medium text-stone-700 hover:border-[hsl(var(--copper))] hover:text-[hsl(var(--copper))] transition disabled:opacity-50"
                                data-testid="lead-copy-client-link"
                            >
                                {copyingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                                Copiar link privado
                            </button>
                            <button
                                type="button"
                                onClick={openClientArea}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[hsl(var(--forest))] text-white text-sm font-medium hover:bg-[hsl(var(--forest-deep))] transition"
                                data-testid="lead-open-client-area"
                            >
                                <ExternalLink className="w-4 h-4" />
                                Abrir Área do Cliente
                            </button>
                        </div>

                        {linkPreview && (
                            <div
                                className="mt-3 p-3 bg-stone-50 rounded-lg text-xs text-stone-600 break-all font-mono"
                                data-testid="lead-client-link-preview"
                            >
                                {linkPreview}
                            </div>
                        )}
                    </div>

                    <LeadConversations
                        lead={selected}
                        onResolved={onConversationResolved}
                        onChanged={reload}
                        onClearLegacy={() => patchField({ has_doubt: false })}
                    />

                    {/* ============ PROPOSTA (Bloco 2) ============ */}
                    <div className="bg-white rounded-lg border border-stone-200 p-4 md:p-5" data-testid="lead-proposal-card">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <FileCheck className="w-4 h-4 text-[hsl(var(--copper))]" />
                            <span className="text-xs uppercase tracking-wider text-stone-700 font-semibold">
                                Proposta
                            </span>
                            {selected.status === "Proposta disponível" && (
                                <span className="text-[10px] uppercase tracking-widest bg-teal-100 text-teal-800 px-2 py-0.5 rounded-full ml-auto" data-testid="lead-proposal-status">
                                    Publicada
                                </span>
                            )}
                            {selected.status === "Adjudicado" && (
                                <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full ml-auto" data-testid="lead-proposal-status">
                                    Adjudicada
                                </span>
                            )}
                            {selected.status === "Não adjudicado" && (
                                <span className="text-[10px] uppercase tracking-widest bg-stone-200 text-stone-700 px-2 py-0.5 rounded-full ml-auto" data-testid="lead-proposal-status">
                                    Não avançou
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-stone-500 mb-4">
                            Carregue os 3 PDFs e publique a proposta. O cliente pode consultar, colocar questões ou adjudicar directamente na Área do Cliente.
                        </p>

                        {selected.status === "Adjudicado" && (
                            <div className="mb-4" data-testid="lead-adjudication-info">
                                <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                                    <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                                    <div className="flex-1 text-sm">
                                        <div className="text-emerald-900 font-semibold">Adjudicação confirmada</div>
                                        <p className="text-emerald-800 text-xs mt-0.5">
                                            Confirmada em {selected.adjudicated_at ? new Date(selected.adjudicated_at).toLocaleString("pt-PT") : "—"} pelo cliente.
                                        </p>
                                        <p className="text-emerald-700 text-[11px] mt-1 leading-snug">
                                            O snapshot dos documentos aceites está preservado. Se substituir os PDFs abaixo, o snapshot original mantém-se intacto.
                                        </p>
                                    </div>
                                </div>
                                {snapshot && (
                                    <details
                                        className="mt-2 bg-stone-50 border border-stone-200 rounded-lg overflow-hidden"
                                        data-testid="lead-adjudication-fingerprint"
                                    >
                                        <summary className="cursor-pointer px-4 py-2.5 text-xs font-semibold text-stone-700 flex items-center gap-2 select-none hover:bg-stone-100">
                                            <Fingerprint className="w-3.5 h-3.5 text-stone-500" />
                                            Impressão digital dos documentos (SHA-256)
                                        </summary>
                                        <div className="px-4 pb-3 space-y-2.5">
                                            {["proposal", "terms", "warranty"].filter((slot) => snapshot[slot]).map((slot) => {
                                                const d = snapshot[slot];
                                                const label = slot === "proposal"
                                                    ? "Proposta / Orçamento"
                                                    : slot === "terms" ? "Condições" : "Garantias";
                                                return (
                                                    <div key={slot} data-testid={`adjudication-sha-${slot}`}>
                                                        <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
                                                            {label}{d.filename ? ` · ${d.filename}` : ""}
                                                        </div>
                                                        <div className="font-mono text-[11px] text-stone-700 break-all leading-tight mt-0.5">
                                                            {d.sha256 || "— hash indisponível (ficheiro ilegível no storage)"}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <div className="pt-1.5 border-t border-stone-200 text-[10px] text-stone-500 leading-snug">
                                                IP {snapshot.ip || "—"} · UA {(snapshot.user_agent || "—").slice(0, 72)}{(snapshot.user_agent || "").length > 72 ? "…" : ""}
                                            </div>
                                        </div>
                                    </details>
                                )}
                            </div>
                        )}

                        {selected.status === "Não adjudicado" && (
                            <div className="mb-4 flex items-start gap-3 bg-stone-50 border border-stone-200 rounded-lg p-4" data-testid="lead-declined-info">
                                <X className="w-5 h-5 text-stone-500 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 text-sm">
                                    <div className="text-stone-900 font-semibold">Cliente não pretendeu avançar</div>
                                    <p className="text-stone-600 text-xs mt-0.5">
                                        Decidido em {selected.declined_at ? new Date(selected.declined_at).toLocaleString("pt-PT") : "—"}.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* File slots — só a Proposta é obrigatória */}
                        <div className="space-y-2 mb-4">
                            <FileSlot label="Proposta / Orçamento" fileId={selected.proposal_file_id} filename={selected.proposal_filename} slot="proposal" patchField={patchField} required />
                            <FileSlot label="Condições Gerais e Particulares" fileId={selected.terms_file_id} filename={selected.terms_filename} slot="terms" patchField={patchField} />
                            <FileSlot label="Garantias" fileId={selected.warranty_file_id} filename={selected.warranty_filename} slot="warranty" patchField={patchField} />
                        </div>

                        {/* Publish button — hidden after adjudication/decline */}
                        {selected.status !== "Adjudicado" && selected.status !== "Não adjudicado" && selected.status !== "Execução" && selected.status !== "Concluído" && (
                            selected.published_at && selected.status === "Proposta disponível" ? (
                                <div className="text-xs text-teal-700 text-center bg-teal-50 border border-teal-200 rounded-lg py-2">
                                    Publicada em {new Date(selected.published_at).toLocaleString("pt-PT")}. O cliente pode consultar.
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    onClick={publishProposal}
                                    disabled={publishingProposal || !selected.proposal_file_id}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[hsl(var(--copper))] text-white text-sm font-semibold hover:opacity-90 transition disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid="lead-publish-proposal"
                                >
                                    {publishingProposal ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck className="w-4 h-4" />}
                                    Publicar proposta
                                </button>
                            )
                        )}
                    </div>

                    {/* ============ EXECUÇÃO (Bloco 3A) ============ */}
                    {(selected.status === "Adjudicado" || selected.status === "Execução" || selected.status === "Concluído") && (
                        <div className="bg-white rounded-lg border border-stone-200 p-4 md:p-5" data-testid="lead-execution-card">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                {selected.status === "Concluído"
                                    ? <Trophy className="w-4 h-4 text-emerald-700" />
                                    : <Hammer className="w-4 h-4 text-[hsl(var(--copper))]" />}
                                <span className="text-xs uppercase tracking-wider text-stone-700 font-semibold">
                                    Execução
                                </span>
                                {selected.status === "Adjudicado" && (
                                    <span className="text-[10px] uppercase tracking-widest bg-stone-100 text-stone-700 px-2 py-0.5 rounded-full ml-auto" data-testid="lead-execution-status">
                                        Por iniciar
                                    </span>
                                )}
                                {selected.status === "Execução" && (
                                    <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full ml-auto" data-testid="lead-execution-status">
                                        Em curso
                                    </span>
                                )}
                                {selected.status === "Concluído" && (
                                    <span className="text-[10px] uppercase tracking-widest bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full ml-auto" data-testid="lead-execution-status">
                                        Concluída
                                    </span>
                                )}
                            </div>

                            {/* Data prevista — editável em Adjudicado, read-only depois */}
                            <div className="mb-4">
                                <label className="block text-sm">
                                    <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">
                                        Data prevista de execução
                                    </span>
                                    {selected.status === "Adjudicado" ? (
                                        <input
                                            type="date"
                                            value={draft.execution_date || ""}
                                            onChange={(e) => setFieldAndSave("execution_date", e.target.value)}
                                            className="w-full px-3 py-3 md:py-2 border border-stone-300 rounded text-sm bg-white"
                                            data-testid="lead-execution-date"
                                        />
                                    ) : (
                                        <div className="px-3 py-2 bg-stone-50 border border-stone-200 rounded text-sm text-stone-700" data-testid="lead-execution-date-ro">
                                            {selected.execution_date || "—"}
                                        </div>
                                    )}
                                </label>
                                {selected.execution_started_at && (
                                    <p className="text-xs text-stone-500 mt-1">
                                        Iniciada em {new Date(selected.execution_started_at).toLocaleString("pt-PT")}.
                                    </p>
                                )}
                                <label className="block text-sm mt-4">
                                    <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">
                                        Conclusão prevista
                                    </span>
                                    {selected.status === "Concluído" ? (
                                        <div className="px-3 py-2 bg-stone-50 border border-stone-200 rounded text-sm text-stone-700" data-testid="lead-expected-completion-ro">
                                            {selected.expected_completion_date || "—"}
                                        </div>
                                    ) : (
                                        <input
                                            type="date"
                                            value={draft.expected_completion_date || ""}
                                            onChange={(e) => setFieldAndSave("expected_completion_date", e.target.value)}
                                            className="w-full px-3 py-3 md:py-2 border border-stone-300 rounded text-sm bg-white"
                                            data-testid="lead-expected-completion"
                                        />
                                    )}
                                    <span className="text-xs text-stone-500 block mt-1">
                                        Previsão visível ao cliente durante a execução. Não envia email.
                                    </span>
                                </label>
                                {selected.completed_at && (
                                    <p className="text-xs text-emerald-700 mt-1">
                                        Concluída em {new Date(selected.completed_at).toLocaleString("pt-PT")}.
                                    </p>
                                )}
                            </div>

                            {/* Action buttons by phase */}
                            {selected.status === "Adjudicado" && (
                                <button
                                    type="button"
                                    onClick={startExecution}
                                    disabled={startingExecution}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[hsl(var(--copper))] text-white text-sm font-semibold hover:opacity-90 transition disabled:opacity-40"
                                    data-testid="lead-start-execution"
                                >
                                    {startingExecution ? <Loader2 className="w-4 h-4 animate-spin" /> : <Hammer className="w-4 h-4" />}
                                    Iniciar execução
                                </button>
                            )}

                            {selected.status === "Execução" && (
                                <div className="space-y-2">
                                    <button
                                        type="button"
                                        onClick={() => setNewUpdateOpen(true)}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[hsl(var(--copper))] text-white text-sm font-semibold hover:opacity-90 transition"
                                        data-testid="lead-new-update"
                                    >
                                        <Camera className="w-4 h-4" />
                                        Nova atualização
                                    </button>
                                    <button
                                        type="button"
                                        onClick={completeExecution}
                                        disabled={completingExecution}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-emerald-300 text-emerald-800 bg-emerald-50 text-sm font-semibold hover:bg-emerald-100 transition disabled:opacity-40"
                                        data-testid="lead-complete-execution"
                                    >
                                        {completingExecution ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
                                        Concluir obra
                                    </button>
                                </div>
                            )}

                            {/* Updates list — visible during Execução and after Concluído */}
                            {(selected.status === "Execução" || selected.status === "Concluído") && (
                                <div className="mt-4 space-y-2" data-testid="lead-updates-list">
                                    <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">
                                        {updates.length} {updates.length === 1 ? "atualização publicada" : "atualizações publicadas"}
                                    </div>
                                    {updates.length === 0 && selected.status === "Execução" && (
                                        <div className="text-xs text-stone-500 italic">
                                            Ainda não publicou nenhuma atualização. O cliente verá uma mensagem simples a dizer que estão a chegar.
                                        </div>
                                    )}
                                    {(showAllUpdates ? updates : updates.slice(0, 3)).map((u) => (
                                        <div key={u.id} className="border border-stone-200 rounded-lg p-3 bg-stone-50/60 flex items-start gap-3" data-testid={`lead-update-${u.id}`}>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[10px] uppercase tracking-wider text-stone-500 font-mono mb-1" data-testid={`lead-update-date-${u.id}`}>
                                                    {formatUpdateDatePT(u.update_date)}
                                                    {u.source === "partner" && <span data-testid={`lead-update-partner-${u.id}`}> · via {u.partner_name || "parceiro"}</span>}
                                                    {(u.photo_file_ids || []).length > 0 && (
                                                        <> · {u.photo_file_ids.length} foto{u.photo_file_ids.length === 1 ? "" : "s"}</>
                                                    )}
                                                </div>
                                                <div className="text-xs text-stone-800 line-clamp-2">
                                                    {u.what_we_did}
                                                </div>
                                            </div>
                                            <div className="flex-shrink-0 flex flex-col sm:flex-row gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingUpdate(u)}
                                                    className="min-h-[44px] px-4 inline-flex items-center justify-center gap-1.5 rounded-full border border-stone-300 text-xs font-semibold text-stone-700 hover:border-[hsl(var(--copper))] hover:text-[hsl(var(--copper))] transition-colors"
                                                    data-testid={`lead-update-edit-${u.id}`}
                                                >
                                                    <Pencil className="w-3.5 h-3.5" /> Editar
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => deleteUpdate(u)}
                                                    disabled={deletingUpdateId === u.id}
                                                    className="min-h-[44px] px-4 inline-flex items-center justify-center gap-1.5 rounded-full border border-red-200 text-xs font-semibold text-red-700 hover:bg-red-50 hover:border-red-300 transition-colors disabled:opacity-40"
                                                    data-testid={`lead-update-delete-${u.id}`}
                                                >
                                                    {deletingUpdateId === u.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Apagar
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    {updates.length > 3 && (
                                        <button
                                            type="button"
                                            onClick={() => setShowAllUpdates((v) => !v)}
                                            className="text-xs text-stone-600 underline min-h-[44px]"
                                            data-testid="lead-updates-toggle-all"
                                        >
                                            {showAllUpdates ? "Mostrar só as 3 mais recentes" : `Ver todas (${updates.length})`}
                                        </button>
                                    )}
                                </div>
                            )}

                            {newUpdateOpen && (
                                <NewUpdateModal
                                    leadId={selected.id}
                                    onClose={() => setNewUpdateOpen(false)}
                                    onPublished={() => { setNewUpdateOpen(false); reloadUpdates(); }}
                                />
                            )}
                            {editingUpdate && (
                                <NewUpdateModal
                                    leadId={selected.id}
                                    update={editingUpdate}
                                    onClose={() => setEditingUpdate(null)}
                                    onPublished={() => { setEditingUpdate(null); reloadUpdates(); }}
                                />
                            )}
                        </div>
                    )}

                    {/* ============ CONSULTA (por defeito) ============ */}
                    {mode === "view" ? (
                        <>
                            <ViewSection title="Contacto">
                                <ViewRow k="Telefone" v={selected.phone} />
                                <ViewRow k="Email" v={selected.email} />
                                <ViewRow k="Localização" v={selected.location} />
                            </ViewSection>
                            <ViewSection title="Pedido">
                                <ViewRow k="Tipo de imóvel" v={selected.property_type} />
                                <ViewRow k="Timing" v={selected.timing} />
                                <ViewRow k="Orçamento estimado" v={selected.budget} />
                                <ViewRow k="Trabalhos" v={(selected.work_types || []).join(", ")} multiline />
                                <ViewRow k="Descrição" v={selected.description} multiline />
                            </ViewSection>
                            <button
                                type="button"
                                onClick={() => setMode("edit")}
                                className="btn-copper !py-3 !px-4 w-full justify-center"
                                data-testid="lead-toggle-edit"
                            >
                                <Pencil className="w-4 h-4" /> Editar dados
                            </button>
                        </>
                    ) : (
                        /* ============ EDIÇÃO ============ */
                        <>
                            <EditSection title="Contacto">
                                <F label="Nome" v={draft.name || ""} on={(v) => setField("name", v)} onBlur={() => saveField("name")} testid="lead-edit-name" />
                                <F label="Telefone" v={draft.phone || ""} on={(v) => setField("phone", v)} onBlur={() => saveField("phone")} type="tel" testid="lead-edit-phone" />
                                <F label="Email" v={draft.email || ""} on={(v) => setField("email", v)} onBlur={() => saveField("email")} type="email" testid="lead-edit-email" />
                                <F label="Localização" v={draft.location || ""} on={(v) => setField("location", v)} onBlur={() => saveField("location")} testid="lead-edit-location" />
                            </EditSection>
                            <EditSection title="Pedido">
                                <label className="block text-sm">
                                    <span className="text-xs uppercase tracking-wider text-stone-500">Tipo de imóvel</span>
                                    <select
                                        value={draft.property_type || ""}
                                        onChange={(e) => setFieldAndSave("property_type", e.target.value)}
                                        className="w-full mt-1 px-3 py-3 md:py-2 border border-stone-300 rounded text-sm bg-white"
                                        data-testid="lead-edit-property"
                                    >
                                        <option value="">—</option>
                                        {PROPERTY_TYPES.map((p) => <option key={p}>{p}</option>)}
                                    </select>
                                </label>
                                <F label="Timing" v={draft.timing || ""} on={(v) => setField("timing", v)} onBlur={() => saveField("timing")} placeholder="ex. próximas 4 semanas" testid="lead-edit-timing" />
                                <F label="Orçamento estimado" v={draft.budget || ""} on={(v) => setField("budget", v)} onBlur={() => saveField("budget")} placeholder="opcional" testid="lead-edit-budget" />
                            </EditSection>
                            <div>
                                <span className="text-xs uppercase tracking-wider text-stone-500 block mb-2">Trabalhos</span>
                                <div className="flex flex-wrap gap-1.5" data-testid="lead-edit-work-types">
                                    {WORK_TYPES.map((w) => {
                                        const on = (draft.work_types || []).includes(w);
                                        return (
                                            <button
                                                type="button"
                                                key={w}
                                                onClick={() => toggleWork(w)}
                                                className={`text-xs px-3 py-2 rounded-full border ${on ? "bg-[hsl(var(--forest))] text-white border-[hsl(var(--forest))]" : "bg-white text-stone-700 border-stone-300"}`}
                                            >
                                                {w}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                            <label className="block text-sm">
                                <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">Descrição do pedido</span>
                                <textarea
                                    rows={4}
                                    value={draft.description || ""}
                                    onChange={(e) => setField("description", e.target.value)}
                                    onBlur={() => saveField("description")}
                                    className="w-full px-3 py-3 md:py-2 border border-stone-300 rounded text-sm"
                                    data-testid="lead-edit-description"
                                />
                            </label>
                            <button
                                type="button"
                                onClick={() => setMode("view")}
                                className="w-full px-4 py-3 border border-stone-300 rounded-full text-sm flex items-center justify-center gap-2"
                                data-testid="lead-toggle-view"
                            >
                                <Check className="w-4 h-4" /> Concluir edição
                            </button>
                        </>
                    )}

                    {/* Foto de capa da obra — independente das fotografias do pedido e das atualizações (leads.cover_file_id) */}
                    <LeadCoverPhoto
                        key={`cover-${selected.id}`}
                        leadId={selected.id}
                        coverId={selected.cover_file_id || null}
                        onChange={(id) => {
                            savedRef.current = { ...savedRef.current, cover_file_id: id };
                            setSelected({ ...savedRef.current });
                            reload();
                        }}
                    />

                    {/* Fotografias do pedido — enviadas pelo cliente no site + adicionadas no Backoffice (leads.file_ids) */}
                    <LeadPhotosSection key={`photos-${selected.id}`} leadId={selected.id} onChanged={reload} />

                    {/* Orçamento (PDF) */}
                    <div>
                        <div className="text-xs uppercase tracking-wider text-stone-500 mb-2 font-medium">Orçamento (PDF)</div>
                        {selected.quote_file_id ? (
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center bg-emerald-50 border border-emerald-200 rounded p-3 gap-2">
                                <a href={fileUrl(selected.quote_file_id)} target="_blank" rel="noreferrer"
                                    className="flex items-center gap-2 text-sm text-emerald-900 hover:underline min-w-0 flex-1"
                                    data-testid={`quote-view-${selected.id}`}>
                                    <FileText className="w-4 h-4 flex-shrink-0" />
                                    <span className="truncate">{selected.quote_filename || "orcamento.pdf"}</span>
                                </a>
                                <div className="flex gap-3 flex-shrink-0 sm:pl-3">
                                    <label className="text-xs text-stone-700 hover:underline cursor-pointer py-1"
                                        data-testid={`quote-replace-${selected.id}`}>
                                        {uploadingQuote === selected.id ? "A carregar…" : "Substituir"}
                                        <input type="file" accept="application/pdf,.pdf" className="hidden"
                                            onChange={(e) => e.target.files[0] && attachQuote(selected.id, e.target.files[0])} />
                                    </label>
                                    <button type="button" onClick={() => detachQuote(selected.id)}
                                        data-testid={`quote-remove-${selected.id}`}
                                        className="text-xs text-red-600 hover:underline py-1">Remover</button>
                                </div>
                            </div>
                        ) : (
                            <label className="flex items-center gap-3 border-2 border-dashed border-stone-300 rounded p-4 md:p-3 cursor-pointer hover:border-[hsl(var(--copper))] bg-stone-50 transition"
                                data-testid={`quote-upload-${selected.id}`}>
                                {uploadingQuote === selected.id ? (
                                    <span className="text-sm text-stone-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> A carregar…</span>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4 text-stone-500" />
                                        <span className="text-sm text-stone-600">Anexar orçamento (PDF)</span>
                                    </>
                                )}
                                <input type="file" accept="application/pdf,.pdf" className="hidden"
                                    onChange={(e) => e.target.files[0] && attachQuote(selected.id, e.target.files[0])} />
                            </label>
                        )}
                        <p className="text-xs text-stone-500 mt-1">Após enviar ao cliente, guarde aqui uma cópia do PDF para consultar depois.</p>
                    </div>

                    {/* Documentos internos — só equipa, separados dos documentos do cliente */}
                    <LeadInternalDocs key={`idocs-${selected.id}`} leadId={selected.id} />

                    {/* Notas internas — autosave onBlur (padrão já existente) */}
                    <div>
                        <div className="text-xs uppercase tracking-wider text-stone-500 mb-2 font-medium">Notas internas</div>
                        <textarea
                            key={selected.id}
                            defaultValue={selected.notes}
                            rows={3}
                            onBlur={(e) => patchField({ notes: e.target.value })}
                            className="w-full border border-stone-300 rounded p-3 md:p-2 text-sm"
                            data-testid="lead-notes"
                        />
                        <p className="text-xs text-stone-500 mt-1">Só visível internamente. Nunca é apresentado ao cliente. Guarda automaticamente ao sair do campo.</p>
                    </div>

                    {/* Próxima ação — placeholder para evolução futura (não implementar workflow agora) */}
                    <div className="border-2 border-dashed border-stone-200 rounded-xl p-4 md:p-5 bg-stone-50" data-testid="lead-next-action">
                        <div className="flex items-center gap-2 mb-2">
                            <ClipboardCheck className="w-4 h-4 text-[hsl(var(--copper))]" />
                            <span className="text-xs uppercase tracking-wider text-stone-700 font-semibold">Próxima ação</span>
                        </div>
                        <p className="text-xs text-stone-500 mb-3">
                            Em breve poderá mover o pedido de fase daqui — registar contactos, agendar visita, preparar orçamento, publicar proposta e mais. Interface reservada; ainda não ativa.
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                            {["Contactei o cliente","Agendar visita","Visita concluída","Preparar orçamento","Publicar proposta"].map((a) => (
                                <button key={a} type="button" disabled
                                    className="text-xs px-3 py-2 rounded-full border border-stone-300 text-stone-500 bg-white opacity-60 cursor-not-allowed"
                                    data-testid={`next-action-${a}`}>{a}</button>
                            ))}
                        </div>
                    </div>

                    {/* Eliminar (discreto no fundo) */}
                    <div className="pt-2">
                        <button type="button" onClick={() => onDelete?.(selected.id)}
                            className="text-xs text-red-600 hover:underline flex items-center gap-1"
                            data-testid="lead-delete">
                            <Trash2 className="w-3 h-3" /> Eliminar pedido
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}



/* ================================================================
   Bloco 3A — Nova atualização da obra
   Mobile-first modal: multi-foto (compressão client-side) + 2 textos
   obrigatórios + nota opcional. Reutiliza /api/upload e resizeImageForEmail.
   ================================================================ */
const todayYMD = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function formatUpdateDatePT(ymd) {
    const [y, m, d] = String(ymd || "").split("-").map((n) => parseInt(n, 10));
    return y && m && d ? new Date(y, m - 1, d).toLocaleDateString("pt-PT", { day: "numeric", month: "long", year: "numeric" }) : "—";
}

/** Criar (sem `update`) ou editar (com `update`) uma atualização da obra — mesmo formulário. */
export function NewUpdateModal({ leadId, update = null, onClose, onPublished }) {
    const isEdit = !!update;
    const today = todayYMD();
    const [updateDate, setUpdateDate] = useState(update?.update_date || today);
    const [photos, setPhotos] = useState(() => (update?.photo_file_ids || []).map((id) => ({ file_id: id, previewUrl: fileUrl(id) })));
    const [whatWeDid, setWhatWeDid] = useState(update?.what_we_did || "");
    const [nextStep, setNextStep] = useState(update?.next_step || "");
    const [note, setNote] = useState(update?.note || "");
    const [publishing, setPublishing] = useState(false);
    const [uploadingCount, setUploadingCount] = useState(0);

    const handleFiles = async (files) => {
        const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
        if (!list.length) return;
        setUploadingCount((c) => c + list.length);
        for (const file of list) {
            let payload = file;
            try {
                payload = await resizeImageForEmail(file, { maxWidth: 1600, maxHeight: 1600, quality: 0.82 });
            } catch { /* fall back to raw file */ }
            try {
                const fd = new FormData();
                fd.append("file", payload);
                const { data } = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setPhotos((prev) => [...prev, {
                    file_id: data.id,
                    filename: payload.name,
                    previewUrl: fileUrl(data.id),
                }]);
            } catch {
                toast.error(`Falha ao carregar ${file.name}`);
            } finally {
                setUploadingCount((c) => c - 1);
            }
        }
    };

    const removePhoto = (fid) => setPhotos((prev) => prev.filter((p) => p.file_id !== fid));

    const publish = async () => {
        if (!whatWeDid.trim() || !nextStep.trim()) return toast.error("Preencha os dois campos obrigatórios.");
        if (!updateDate || updateDate > today) return toast.error("A data da atualização não pode ser no futuro.");
        setPublishing(true);
        const body = {
            update_date: updateDate,
            photo_file_ids: photos.map((p) => p.file_id),
            what_we_did: whatWeDid.trim(),
            next_step: nextStep.trim(),
            note: note.trim() || (isEdit ? "" : undefined),
        };
        try {
            if (isEdit) {
                await api.patch(`/admin/leads/${leadId}/updates/${update.id}`, body);
                toast.success("Alterações guardadas.");
            } else {
                await api.post(`/admin/leads/${leadId}/updates`, body);
                toast.success("Atualização publicada.");
            }
            onPublished();
        } catch (e) {
            toast.error(e?.response?.data?.detail || (isEdit ? "Erro ao guardar" : "Erro ao publicar"));
        } finally { setPublishing(false); }
    };

    const canPublish = !publishing && uploadingCount === 0 && !!whatWeDid.trim() && !!nextStep.trim() && !!updateDate && updateDate <= today;

    return (
        <div
            className="fixed inset-0 z-[60] bg-white md:bg-stone-950/60 md:flex md:items-center md:justify-center md:p-4"
            data-testid="new-update-modal"
        >
            <div className="bg-white w-full h-full md:h-auto md:max-w-xl md:max-h-[90vh] md:rounded-xl md:shadow-2xl flex flex-col overflow-hidden">
                <div className="sticky top-0 z-10 bg-white border-b border-stone-100 px-4 py-3 flex items-center gap-3">
                    <button
                        onClick={onClose}
                        className="p-2 -ml-1 rounded-full hover:bg-stone-100 min-w-[44px] min-h-[44px] flex items-center justify-center"
                        data-testid="new-update-back"
                        aria-label="Voltar"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex-1 min-w-0">
                        <div className="font-display font-bold text-base md:text-lg">{isEdit ? "Editar atualização" : "Nova atualização"}</div>
                        <div className="text-xs text-stone-500">{isEdit ? "Corrija textos, data ou fotografias" : "Data + 2 linhas rápidas + fotos"}</div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-5 pb-28">
                    <label className="block text-sm">
                        <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">
                            Data da atualização <span className="text-red-500">*</span>
                        </span>
                        <input
                            type="date"
                            value={updateDate}
                            max={today}
                            onChange={(e) => setUpdateDate(e.target.value)}
                            className="w-full min-h-[48px] px-3 py-2 border border-stone-300 rounded-lg text-base bg-white"
                            data-testid="update-date-input"
                        />
                        <span className="text-xs text-stone-500 mt-1 block">Dia a que os trabalhos dizem respeito. Não pode ser uma data futura.</span>
                    </label>

                    <label className="block text-sm">
                        <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">
                            O que fizemos <span className="text-red-500">*</span>
                        </span>
                        <textarea
                            rows={3}
                            value={whatWeDid}
                            onChange={(e) => setWhatWeDid(e.target.value)}
                            placeholder="Ex.: Concluímos as alterações de canalização e iniciámos a preparação das paredes."
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm resize-none"
                            data-testid="update-what-we-did"
                        />
                    </label>

                    <label className="block text-sm">
                        <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">
                            O que acontece a seguir <span className="text-red-500">*</span>
                        </span>
                        <textarea
                            rows={2}
                            value={nextStep}
                            onChange={(e) => setNextStep(e.target.value)}
                            placeholder="Ex.: Aplicação dos revestimentos."
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm resize-none"
                            data-testid="update-next-step"
                        />
                    </label>

                    <label className="block text-sm">
                        <span className="text-xs uppercase tracking-wider text-stone-500 block mb-1">
                            Nota (opcional)
                        </span>
                        <input
                            type="text"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Opcional"
                            className="w-full px-3 py-2 border border-stone-300 rounded-lg text-sm"
                            data-testid="update-note"
                        />
                    </label>

                    <div>
                        <div className="text-xs uppercase tracking-wider text-stone-500 font-semibold mb-2">
                            Fotografias{photos.length > 0 ? ` · ${photos.length}` : ""}
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            {photos.map((p) => (
                                <div key={p.file_id} className="relative group" data-testid={`new-update-photo-${p.file_id}`}>
                                    <img src={p.previewUrl} alt="" className="w-full aspect-square object-cover rounded-lg border border-stone-200" />
                                    <button
                                        type="button"
                                        onClick={() => removePhoto(p.file_id)}
                                        className="absolute top-1 right-1 w-9 h-9 rounded-full bg-black/60 text-white flex items-center justify-center"
                                        aria-label="Remover fotografia"
                                        data-testid={`new-update-photo-remove-${p.file_id}`}
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                            {uploadingCount > 0 && (
                                <div className="aspect-square rounded-lg border-2 border-dashed border-stone-300 bg-stone-50 flex items-center justify-center">
                                    <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
                                </div>
                            )}
                            <label
                                className="aspect-square rounded-lg border-2 border-dashed border-stone-300 bg-stone-50 flex flex-col items-center justify-center text-center px-1 cursor-pointer hover:border-[hsl(var(--copper))] hover:bg-[hsl(var(--cream))]/50 transition text-stone-500"
                                data-testid="new-update-add-photo"
                            >
                                <Camera className="w-5 h-5 mb-1" />
                                <span className="text-[10px] uppercase tracking-wider leading-tight">+ Adicionar fotografias</span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className="hidden"
                                    onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
                                    data-testid="new-update-add-photo-input"
                                />
                            </label>
                        </div>
                        <p className="text-xs text-stone-500 mt-2">
                            As fotografias são comprimidas automaticamente para 1600 px (JPEG 82%) antes de enviar.
                        </p>
                    </div>
                </div>

                <div className="sticky bottom-0 border-t border-stone-100 bg-white p-3 flex gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-3 rounded-full border border-stone-300 text-sm text-stone-700"
                        data-testid="update-cancel"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={publish}
                        disabled={!canPublish}
                        className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[hsl(var(--copper))] text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="update-publish"
                    >
                        {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        {isEdit ? "Guardar alterações" : "Publicar atualização"}
                    </button>
                </div>
            </div>
        </div>
    );
}
